import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Super Admin + Back Office: fix the customer's e-mail / phone for THIS order.
// Shared customer record → a separate record is created for this order only.
export function EditContactButton({ order, testid = "edit-contact", compact = false }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const { data } = useQuery({
    queryKey: ["order-contact", order.id],
    queryFn: async () => (await api.get(`/orders/${order.id}/customer-contact`)).data,
    enabled: open,
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/orders/${order.id}/customer-contact`, { email, phone }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      setOpen(false);
      toast.success(r.data.split ? t("Contact saved — separate customer record created for this order") : t("Customer contact updated"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update"),
  });
  const openDialog = () => { setEmail(order.customer?.email || ""); setPhone(order.customer?.phone || ""); setOpen(true); };
  const others = data?.other_orders || 0;
  return (
    <>
      {compact ? (
        <button type="button" onClick={openDialog} title={t("Edit customer e-mail / phone")} className="text-slate-400 hover:text-slate-700 leading-none" data-testid={`${testid}-btn`}>✉</button>
      ) : (
        <Button variant="outline" size="sm" onClick={openDialog} data-testid={`${testid}-btn`}>✎ {t("Edit e-mail / phone")}</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>{t("Edit customer e-mail / phone")}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">{order.po_number} · {order.customer?.name} · {t("Super Admin & Back Office")}</p>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-widest">{t("E-mail")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" autoFocus data-testid={`${testid}-email`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-widest">{t("Phone")}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" data-testid={`${testid}-phone`} />
            </div>
            {others > 0 ? (
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`${testid}-split-hint`}>
                ⚠ {t("This customer record is shared with")} {others} {t("other order(s). Saving creates a separate customer record for this order only — the other orders keep the current contact.")}
              </div>
            ) : data ? (
              <div className="text-xs text-slate-500" data-testid={`${testid}-inplace-hint`}>{t("Only this order uses this customer record — it will be updated in place.")}</div>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>{t("Cancel")}</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || (!email.trim() && !phone.trim())} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`${testid}-save`}>
              {save.isPending ? t("Saving…") : t("Save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
